// Room-scene catalogue for the "See it in your space" feature.
//
// v1.30: scenes are now sourced from the backend (`GET /api/rooms`) so admins
// can re-render any individual room with a new prompt or tune slot coords
// without a deploy. The hardcoded DEFAULT_SCENES below is the seed catalogue
// — used as fallback if the API is unreachable, and seeded into Mongo on
// backend startup via room_renderer.DEFAULT_SCENES.

import { useEffect, useState } from "react";
import api from "./api";

// Cache-busted image URLs come from the API, so DEFAULT_SCENES uses static
// paths (no version param) just as a render-before-fetch fallback.
const DEFAULT_SCENES = [
  {
    scene_id: "coastal-living",
    label: "Coastal living room",
    image_url: "/rooms/coastal-living.png",
    image_aspect: 1200 / 896,
    slot: { top: 12, left: 28, width: 42, height: 38 },
    wall_tone: "#EFE9DC",
  },
  {
    scene_id: "warm-savannah",
    label: "Warm savannah",
    image_url: "/rooms/warm-savannah.png",
    image_aspect: 1200 / 896,
    slot: { top: 8, left: 33, width: 36, height: 36 },
    wall_tone: "#C18468",
  },
  {
    scene_id: "modern-loft",
    label: "Modern loft",
    image_url: "/rooms/modern-loft.png",
    image_aspect: 1200 / 896,
    slot: { top: 8, left: 28, width: 38, height: 42 },
    wall_tone: "#A5A4A0",
  },
  {
    scene_id: "study-nook",
    label: "Editorial study",
    image_url: "/rooms/study-nook.png",
    image_aspect: 1200 / 896,
    slot: { top: 8, left: 38, width: 32, height: 36 },
    wall_tone: "#EFE9DD",
  },
  {
    scene_id: "atelier-loft",
    label: "Artist atelier",
    image_url: "/rooms/atelier-loft.png",
    image_aspect: 1200 / 896,
    slot: { top: 25, left: 18, width: 48, height: 48 },
    wall_tone: "#E9E2D6",
  },
  {
    scene_id: "collector-foyer",
    label: "Collector's foyer",
    image_url: "/rooms/collector-foyer.png",
    image_aspect: 1200 / 896,
    slot: { top: 16, left: 22, width: 54, height: 50 },
    wall_tone: "#9B5A47",
  },
];

// Adapter: backend returns snake_case (image_url, image_aspect, wall_tone),
// components expect a slightly different shape. Normalise here so renderers
// can be agnostic about the source.
function normalise(scene) {
  return {
    id: scene.scene_id,
    type: scene.scene_type || "wall",  // v1.40 — "wall" | "plinth"
    label: scene.label,
    image: scene.image_url,
    imageAspect: scene.image_aspect,
    slot: scene.slot,
    wallTone: scene.wall_tone,
  };
}

// Module-level cache + subscriber set so multiple components share a single
// fetch and re-render together when the catalogue changes (e.g. after an
// admin regenerates a scene in another tab).
let _cache = DEFAULT_SCENES.map(normalise);
let _loadPromise = null;
const _listeners = new Set();

async function loadOnce() {
  if (_loadPromise) return _loadPromise;
  _loadPromise = api
    .get("/rooms")
    .then((r) => {
      if (Array.isArray(r.data) && r.data.length) {
        _cache = r.data.map(normalise);
        _listeners.forEach((fn) => fn(_cache));
      }
      return _cache;
    })
    .catch(() => _cache);
  return _loadPromise;
}

/** Reset the loader — call after an admin regenerate to force a fresh fetch. */
export function invalidateRoomScenes() {
  _loadPromise = null;
}

/** Return `true` if the given `medium` string describes a 3D work.
 *  Mirror of the backend's `is_sculpture()`. Keep the keyword lists in sync.  */
export function isSculpture(medium) {
  if (!medium) return false;
  const m = medium.toLowerCase();
  return [
    "sculpture", "sculptural", "ceramic", "clay", "bronze", "brass",
    "wood carving", "wood-carving", "stone", "marble", "installation",
    "assemblage", "3d", "three-dimensional", "curio", "curios",
    "figurine", "totem", "carving",
  ].some((k) => m.includes(k));
}

/** Mirror of backend `is_digital()` — digital / screen-native works. */
export function isDigital(medium) {
  if (!medium) return false;
  const m = medium.toLowerCase();
  return [
    "digital", "video", "animation", "generative", "nft",
    "gif", "motion", "screen", "3d render", "cgi",
  ].some((k) => m.includes(k));
}

/** Return the visualisation scene_type for a given artwork medium. */
export function sceneTypeForMedium(medium) {
  if (isDigital(medium)) return "screen";
  if (isSculpture(medium)) return "plinth";
  return "wall";
}

/** React hook — returns the current scene catalogue. Pass `medium` to
 *  receive only scenes appropriate for that artwork type. Omit for full list. */
export function useRoomScenes(medium) {
  const [scenes, setScenes] = useState(_cache);
  useEffect(() => {
    _listeners.add(setScenes);
    loadOnce();
    return () => _listeners.delete(setScenes);
  }, []);
  if (medium === undefined) return scenes;
  const wanted = sceneTypeForMedium(medium);
  return scenes.filter((s) => (s.type || "wall") === wanted);
}

// Frame chrome presets — independent of the scene catalogue.
export const FRAME_STYLES = [
  { id: "oak", label: "Light oak", moulding: "#A98A66", mouldingDark: "#7E6849", mat: "#F4EFE6", mouldingPx: 14, matPx: 18 },
  { id: "black", label: "Matte black", moulding: "#1A1816", mouldingDark: "#0B0A09", mat: "#F8F4EC", mouldingPx: 14, matPx: 16 },
  { id: "gold", label: "Antique gold", moulding: "#C4A04B", mouldingDark: "#8C6F2D", mat: "#F1E8D4", mouldingPx: 16, matPx: 20 },
  { id: "none", label: "Unframed", moulding: null, mouldingDark: null, mat: null, mouldingPx: 0, matPx: 0 },
];

export const DEFAULT_FRAME_ID = "oak";

// Backwards-compat for code paths still importing `ROOM_SCENES` directly.
// Returns the current cache (defaults until the API fetch completes).
export const ROOM_SCENES = new Proxy([], {
  get(_, prop) {
    return Reflect.get(_cache, prop);
  },
  has(_, prop) {
    return Reflect.has(_cache, prop);
  },
  ownKeys() {
    return Reflect.ownKeys(_cache);
  },
  getOwnPropertyDescriptor(_, prop) {
    return Reflect.getOwnPropertyDescriptor(_cache, prop);
  },
});
